/**
 * @module  public/js/services/focusModeTaskService.js
 * @class   FocusModeTaskService
 * @summary Subscribe to mirrored tasks for Focus Mode and persist status changes.
 *
 * @description
 * Firestore-first, aligned with Prod V1:
 *  • Subscribes to `/tenants/{tenantId}/mirrorTasks` for the signed-in user's
 *    OPEN / IN_PROGRESS tasks, ordered by createdAt; due ordering handled
 *    client-side.
 *  • Completes tasks via `taskCompletionService.completeWithOutcome` when
 *    logging outcomes, otherwise writes directly to Firestore.
 *  • Legacy TasksStore fallback is **disabled by default** to avoid Dev CORS noise.
 *
 * ## Public API
 * – `subscribe(cb)` – listen to task snapshots for the signed-in user
 * – `startTask(taskId)` – mark task as **IN_PROGRESS**
 * – `completeTask(taskId, notes?, opts?)` – mark task as **COMPLETED**, optionally log outcome
 *
 * @author  TopSail AI
 * @version 1.1.0
 * @since   2025-05-26
 */

import {
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  getDoc,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';

import {
  colWithTenant,
  docWithTenant,
  writeWithTenant,
} from '../utils/firestoreHelpers.js';
import { parseMeta } from '../utils/taskMeta.js';
import { getTaskDueMillis } from '../utils/taskUtils.js';
import { normalizeInFlightQueuePolicy } from '../focusmode/inFlightQueuePolicy.js';

import TenantResolver from './tenantResolver.js';

/** Concurrency cap for defer-to-tomorrow writes to avoid UI stalls. */
export const DEFER_WRITE_CONCURRENCY = 6;

/**
 * Run async work with a fixed concurrency limit.
 *
 * @template T, R
 * @param {Array<T>} items - Items to process.
 * @param {number} limit - Maximum concurrent workers.
 * @param {(item: T) => Promise<R>} worker - Async worker function.
 * @returns {Promise<Array<R>>} Aggregated worker results.
 */
async function runWithConcurrency(items, limit, worker) {
  const max = Math.max(1, Number.isFinite(limit) ? limit : 1);
  let idx = 0;

  const takeNext = () => {
    if (idx >= items.length) return null;
    const current = idx;
    idx += 1;
    return items[current];
  };

  const runners = Array.from(
    { length: Math.min(max, items.length) },
    async () => {
      for (;;) {
        const item = takeNext();
        if (item === null) break;
        // eslint-disable-next-line no-await-in-loop
        await worker(item);
      }
    }
  );

  await Promise.all(runners);
  return [];
}

/**
 * Return a yyyy-mm-dd string for the given date in the specified timezone.
 * Falls back to the browser-local timezone when `tz` is falsy.
 *
 * @param {Date|number|string} dateLike - Date object, epoch ms, or ISO string.
 * @param {string|null|undefined} tz - IANA timezone identifier.
 * @returns {string} Formatted date key or an empty string when invalid.
 */
export function dateKeyInTz(dateLike, tz) {
  const d =
    dateLike instanceof Date
      ? dateLike
      : typeof dateLike === 'number' || typeof dateLike === 'string'
        ? new Date(dateLike)
        : null;
  if (!d || Number.isNaN(d.getTime())) return '';

  let formatter;
  try {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (err) {
    console.warn('[FocusModeTaskService] invalid timezone', { tz, err });
    return '';
  }
  const parts = formatter.formatToParts(d);
  const lookup = (type) => parts.find((p) => p.type === type)?.value || '';
  const year = lookup('year');
  const month = lookup('month');
  const day = lookup('day');
  return year && month && day ? `${year}-${month}-${day}` : '';
}

export default class FocusModeTaskService {
  /** @type {import('../services/authservice.js').default} */
  #auth;
  /** @type {import('../stores/tasksstore.js').default | undefined} */
  #tasksStore;
  /** @type {import('./taskcompletionservice.js').default | undefined} */
  #taskCompletionService;
  /** @type {import('./tenantResolver.js').default} */
  #tenantResolver;

  /** @type {{ tz: string|null, effectiveMax: number|null, inFlightPolicy:object } | null} */
  #queuePrefs = null;

  /** Cached once to eliminate tenant races */
  #tenantId;

  /** Toggle legacy POSTs (Cloud Run). Default false to avoid CORS in Dev. */
  #enableLegacyFallback = false;

  /** @type {Function|null} Server-side defer callable. */
  #deferFocusModeTasksCallable = null;

  /** Track defer runs in-flight per day/reason to avoid re-entrance. */
  #deferInFlightByISO = new Set();

  /**
   * @param {object} deps
   * @param {import('../services/authservice.js').default} deps.auth
   * @param {import('../stores/tasksstore.js').default=} deps.tasksStore Deprecated TasksStore fallback
   * @param {import('./taskcompletionservice.js').default=} deps.taskCompletionService Optional completion helper
   * @param {Function=} deps.deferFocusModeTasks Server-side deferral callable
   * @param {{ enableLegacyFallback?: boolean }=} opts
   */
  constructor(
    {
      auth,
      tasksStore,
      taskCompletionService,
      tenantResolver,
      deferFocusModeTasks,
      callables,
    },
    opts = {}
  ) {
    this.#auth = auth;
    this.#tasksStore = tasksStore;
    this.#taskCompletionService = taskCompletionService;
    this.#tenantResolver = tenantResolver || new TenantResolver(auth);
    this.#deferFocusModeTasksCallable =
      deferFocusModeTasks || callables?.deferFocusModeTasks || null;
    this.#enableLegacyFallback = !!opts.enableLegacyFallback;
  }

  /**
   * Expose the cached user timezone when available.
   *
   * @returns {string|null} IANA timezone identifier or null.
   */
  getUserTimezone() {
    return this.#queuePrefs?.tz || null;
  }

  /**
   * Expose the effective queue max derived from CapacityBuckets.
   *
   * @returns {number|null} Positive number when enabled; otherwise null.
   */
  getEffectiveQueueMax() {
    return typeof this.#queuePrefs?.effectiveMax === 'number'
      ? this.#queuePrefs.effectiveMax
      : null;
  }

  /**
   * Expose the independent daily limits for sequences already in flight.
   *
   * @returns {{enabled:boolean,rules:Array<{playbookId:string,dailyMax:number}>}} Normalized policy.
   */
  getInFlightQueuePolicy() {
    return (
      this.#queuePrefs?.inFlightPolicy ||
      normalizeInFlightQueuePolicy({ enabled: false, rules: [] })
    );
  }

  /**
   * Lazily resolve and cache the tenantId.
   * Uses the authed user if available; awaits login otherwise.
   * Returns null if tenant cannot be resolved (fail-soft).
   * @returns {Promise<string|null>}
   */
  async #getTenantId() {
    if (this.#tenantId) return this.#tenantId;
    try {
      const tid = await this.#tenantResolver.resolveTenantId();
      if (!tid) {
        console.warn(
          '[FocusModeTaskService] no tenant (user loaded, tenantId missing)'
        );
        return null;
      }
      this.#tenantId = tid;
      return tid;
    } catch (err) {
      console.warn('[FocusModeTaskService] tenant resolve failed', err);
      return null;
    }
  }

  /**
   * Fetch queue-related preferences for the signed-in user.
   * Reads CapacityBuckets for WIP/QueueMax, UserPrefs for timezone, and the
   * independent QueueMixPolicies in-flight limits.
   * Defaults to disabled queue controls on failure.
   *
   * @param {string} tenantId - Active tenant identifier.
   * @param {string} uid - User identifier.
   * @returns {Promise<{ tz: string|null, effectiveMax: number|null, inFlightPolicy:object }>}
   */
  async #loadQueuePrefs(tenantId, uid) {
    const safeNum = (val, fallback = null) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : fallback;
    };

    try {
      const [capSnap, prefSnap, queueMixSnap] = await Promise.all([
        getDoc(docWithTenant(tenantId, `CapacityBuckets/${uid}`)),
        getDoc(docWithTenant(tenantId, `UserPrefs/${uid}`)),
        getDoc(docWithTenant(tenantId, `QueueMixPolicies/${uid}`)),
      ]);
      const cap = capSnap.exists() ? capSnap.data() || {} : {};
      const wip = safeNum(cap.wip, 10);
      const queueMaxEnabled = cap.queueMaxEnabled === true;
      const queueMax = safeNum(cap.queueMax, null);
      const effectiveMax = queueMaxEnabled
        ? Math.max(queueMax ?? wip ?? 10, wip ?? 10)
        : null;

      const tz = prefSnap.exists() ? prefSnap.data()?.tz || null : null;
      const inFlightPolicy = normalizeInFlightQueuePolicy(
        queueMixSnap.exists() ? queueMixSnap.data()?.inFlight || {} : {}
      );

      this.#queuePrefs = { tz, effectiveMax, inFlightPolicy };
      return this.#queuePrefs;
    } catch (err) {
      console.warn('[FocusModeTaskService] queue pref load failed', err);
      this.#queuePrefs = {
        tz: null,
        effectiveMax: null,
        inFlightPolicy: normalizeInFlightQueuePolicy({}),
      };
      return this.#queuePrefs;
    }
  }

  /**
   * Defer the provided tasks to tomorrow while marking them as "deferred from" today.
   * This preserves the current due timestamp so allocator reclaim can restore it
   * for queue-cap deferrals while also supporting other defer reasons.
   *
   * @param {Array<Record<string, unknown>>} tasks - Task payloads to move.
   * @param {{
   *   fromISO: string,
   *   tz?: string|null,
   *   deferredBy?: string,
   *   metadata?: Record<string, unknown>,
   * }} opts - Day-key metadata and defer marker.
   * @returns {Promise<Array<string>>} Task ids successfully deferred.
   */
  async deferTasksToTomorrow(
    tasks,
    { fromISO, tz = null, deferredBy = 'balanceTomorrow', metadata = {} } = {}
  ) {
    if (!Array.isArray(tasks) || tasks.length === 0 || !fromISO) return [];
    const deferKey = `${String(deferredBy || 'balanceTomorrow')}:${fromISO}`;
    if (this.#deferInFlightByISO.has(deferKey)) return [];
    this.#deferInFlightByISO.add(deferKey);

    try {
      const tenantId = await this.#getTenantId();
      if (!tenantId) return [];
      const user =
        this.#auth?.currentUser ||
        (typeof this.#auth?.waitForLogin === 'function'
          ? await this.#auth.waitForLogin().catch(() => null)
          : null);
      const uid = user?.uid || '';
      if (!uid) return [];

      const tasksToDefer = tasks.filter((task) => {
        if (!task?.id) return false;
        const meta = task.metadata || {};
        const nestedMeta = meta.metadata || {};
        const existingDeferredBy =
          task.deferredBy ?? meta.deferredBy ?? nestedMeta.deferredBy;
        const deferredFromISO =
          task.deferredFromISO ??
          meta.deferredFromISO ??
          nestedMeta.deferredFromISO;
        return !(
          existingDeferredBy === deferredBy && deferredFromISO === fromISO
        );
      });
      if (tasksToDefer.length === 0) return [];
      if (typeof this.#deferFocusModeTasksCallable !== 'function') {
        console.warn(
          '[FocusModeTaskService] defer callable unavailable; skipping deferral'
        );
        return [];
      }

      const response = await this.#deferFocusModeTasksCallable({
        tenantId,
        uid,
        taskIds: tasksToDefer.map((task) => task.id),
        fromISO,
        tz,
        deferredBy,
        metadata,
      });
      return Array.isArray(response?.data?.deferredTaskIds)
        ? response.data.deferredTaskIds
        : [];
    } catch (err) {
      console.warn('[FocusModeTaskService] deferTasksToTomorrow failed', err);
      return [];
    } finally {
      this.#deferInFlightByISO.delete(deferKey);
    }
  }

  /**
   * Persist same-day availability holds for tasks that should re-enter Focus Mode
   * later today instead of being deferred to tomorrow.
   *
   * @param {Array<{
   *   task: Record<string, unknown>,
   *   holdUntilAt: string,
   *   metadata?: Record<string, unknown>,
   * }>} entries - Per-task hold definitions with absolute release timestamps.
   * @param {{
   *   holdBy?: string,
   * }} [options={}] - Hold marker metadata.
   * @returns {Promise<Array<string>>} Task ids successfully placed on hold.
   */
  async holdTasksUntil(
    entries,
    { holdBy = 'contactLocalCallWindowEarly' } = {}
  ) {
    if (!Array.isArray(entries) || entries.length === 0) return [];

    const tenantId = await this.#getTenantId();
    if (!tenantId) return [];

    const holdEntries = entries.filter((entry) => {
      const taskId = String(entry?.task?.id || '').trim();
      const holdUntilAt = String(entry?.holdUntilAt || '').trim();
      if (!taskId || !holdUntilAt) return false;

      const task = entry.task || {};
      const meta = task.metadata || {};
      const nestedMeta = meta.metadata || {};
      const existingHoldBy =
        task.availabilityHoldBy ??
        meta.availabilityHoldBy ??
        nestedMeta.availabilityHoldBy;
      const existingHoldUntilAt =
        task.availabilityHoldUntilAt ??
        meta.availabilityHoldUntilAt ??
        nestedMeta.availabilityHoldUntilAt;
      return !(
        String(existingHoldBy || '') === String(holdBy || '') &&
        String(existingHoldUntilAt || '') === holdUntilAt
      );
    });
    if (holdEntries.length === 0) return [];

    const heldIds = [];
    await runWithConcurrency(
      holdEntries,
      DEFER_WRITE_CONCURRENCY,
      async (entry) => {
        const taskId = String(entry?.task?.id || '').trim();
        if (!taskId) return taskId;

        const payload = {
          availabilityHoldBy: holdBy,
          availabilityHoldUntilAt: String(entry?.holdUntilAt || '').trim(),
          availabilityHoldSetAt: serverTimestamp(),
          ...(entry?.metadata && typeof entry.metadata === 'object'
            ? entry.metadata
            : {}),
        };

        const mirrorRef = docWithTenant(tenantId, `mirrorTasks/${taskId}`);
        const taskRef = docWithTenant(tenantId, `tasks/${taskId}`);
        try {
          await Promise.all([
            writeWithTenant(tenantId, mirrorRef, payload, { merge: true }),
            writeWithTenant(tenantId, taskRef, payload, { merge: true }),
          ]);
          heldIds.push(taskId);
        } catch (err) {
          console.warn('[FocusModeTaskService] holdTasksUntil write failed', {
            taskId,
            err,
          });
        }
        return taskId;
      }
    );

    return heldIds;
  }

  /**
   * Subscribe to the current user's OPEN / IN_PROGRESS tasks.
   * Caches tenantId so later writes never re-resolve it mid-flight.
   * @param {(tasks: Array<object>) => void} cb
   * @param {(err: Error) => void=} onError
   * @returns {() => void} unsubscribe handler
   */
  subscribe(cb, onError) {
    const me = this.#auth.currentUser;
    if (!me) throw new Error('FocusModeTaskService.subscribe: no auth user');

    let unsub = () => {};
    (async () => {
      const tenantId = await this.#getTenantId();
      if (!tenantId) {
        console.warn(
          'FocusModeTaskService.subscribe: missing tenant (returning empty set)'
        );
        try {
          cb([]);
        } catch {
          /* noop */
        }
        return;
      }
      this.#tenantId = tenantId; // cache for writers
      await this.#loadQueuePrefs(tenantId, me.uid);
      const base = colWithTenant(tenantId, 'mirrorTasks');

      const q = query(
        base,
        where('assignedToUid', '==', me.uid),
        where('status', 'in', ['OPEN', 'IN_PROGRESS']),
        orderBy('createdAt', 'asc')
      );
      unsub = onSnapshot(
        q,
        (snap) => {
          const tasks = snap.docs
            .map((d) => {
              const data = d.data();

              const metaType =
                data.metadata?.type ||
                parseMeta(data.notes)?.type ||
                parseMeta(data.metadata?.notes)?.type ||
                data.type;
              return {
                id: d.id,
                ...data,
                type: metaType || data.type || 'UNKNOWN',
              };
            })
            .filter((task) => {
              const meta = task.metadata || {};
              return !(
                meta.focusmodeHidden === true ||
                meta.focusModeHidden === true ||
                meta.origin === 'followup'
              );
            })
            .sort((a, b) => {
              const dueA = getTaskDueMillis(a);
              const dueB = getTaskDueMillis(b);
              if (dueA !== null && dueB !== null && dueA !== dueB) {
                return dueA - dueB;
              }
              if (dueA !== null && dueB === null) return -1;
              if (dueA === null && dueB !== null) return 1;
              const createdA =
                a.createdAt?.toMillis?.() ??
                a.createdAt?.toDate?.()?.getTime?.() ??
                (typeof a.createdAt?._seconds === 'number'
                  ? a.createdAt._seconds * 1000 +
                    Math.floor((a.createdAt._nanoseconds || 0) / 1e6)
                  : null) ??
                (a.createdAt instanceof Date
                  ? a.createdAt.getTime()
                  : typeof a.createdAt === 'number'
                    ? a.createdAt
                    : 0);
              const createdB =
                b.createdAt?.toMillis?.() ??
                b.createdAt?.toDate?.()?.getTime?.() ??
                (typeof b.createdAt?._seconds === 'number'
                  ? b.createdAt._seconds * 1000 +
                    Math.floor((b.createdAt._nanoseconds || 0) / 1e6)
                  : null) ??
                (b.createdAt instanceof Date
                  ? b.createdAt.getTime()
                  : typeof b.createdAt === 'number'
                    ? b.createdAt
                    : 0);
              return createdA - createdB;
            });

          cb(tasks);
        },
        (err) => {
          console.error(
            '[FocusModeTaskService] subscribe onSnapshot error',
            err
          );
          try {
            onError?.(err);
          } catch {
            /* noop */
          }
        }
      );
    })().catch((err) => {
      console.warn('[FocusModeTaskService] subscribe init failed', err);
      try {
        onError?.(err);
      } catch {
        /* noop */
      }
    });
    return () => {
      try {
        unsub();
      } catch {
        /* noop */
      }
    };
  }

  /**
   * Mark a task as **IN_PROGRESS** (Firestore authoritative).
   * Fail-soft if tenant is not yet cached/available.
   * @param {string} taskId
   * @returns {Promise<void>}
   */
  async startTask(taskId) {
    const tenantId = await this.#getTenantId();
    if (!tenantId) {
      console.warn(
        'FocusModeTaskService.startTask: missing tenant (skipping)',
        { taskId }
      );
      return;
    }

    const ref = docWithTenant(tenantId, `mirrorTasks/${taskId}`);
    await writeWithTenant(
      tenantId,
      ref,
      { status: 'IN_PROGRESS', startedAt: serverTimestamp() },
      { merge: true }
    ).catch((err) => {
      console.warn('FocusModeTaskService.startTask: mirror write failed', {
        taskId,
        err,
      });
    });

    if (this.#enableLegacyFallback) {
      await this.#tasksStore
        ?.updateTask?.(taskId, { status: 'IN_PROGRESS' })
        .catch(() => {});
    }
  }

  /**
   * Mark a task as **COMPLETED**, optionally logging an outcome.
   * Fail-soft if tenant is not yet cached/available.
   * @param {string} taskId
   * @param {string=} notes
   * @param {
   *   logOutcome?: boolean,
   *   outcome?: string,
   *   metadata?: Object,
   *   tenantIdOverride?: string,
   *   endSequence?: boolean,
   * } [opts]
   * @returns {Promise<void>}
   */
  async completeTask(
    taskId,
    notes,
    {
      logOutcome = true,
      outcome = 'TASK_COMPLETE',
      metadata = {},
      tenantIdOverride,
      endSequence = false,
    } = {}
  ) {
    const tenantId = tenantIdOverride ?? (await this.#getTenantId());

    if (!tenantId) {
      console.warn(
        'FocusModeTaskService.completeTask: missing tenant (skipping update)',
        { taskId }
      );
      return;
    }

    const user =
      this.#auth.currentUser ??
      (typeof this.#auth.waitForLogin === 'function'
        ? await this.#auth.waitForLogin().catch(() => null)
        : null);
    if (!user) {
      console.warn(
        'FocusModeTaskService.completeTask: auth missing (skipping)',
        {
          taskId,
        }
      );
      return;
    }
    try {
      await user.getIdToken?.(!tenantId);
    } catch {
      /* noop */
    }

    if (logOutcome) {
      try {
        await this.#taskCompletionService?.completeWithOutcome(
          null,
          {
            taskId,
            outcome,
            metadata: notes ? { ...metadata, notes } : metadata,
            endSequence,
          },
          { tenantIdOverride: tenantId }
        );
      } catch (err) {
        console.warn('FocusModeTaskService.completeTask: outcome log failed', {
          taskId,
          err,
        });
      }
      return; // Firestore updated via completeWithOutcome
    }

    try {
      const isMirrorTaskId =
        typeof taskId === 'string' && taskId.startsWith('mt_');
      if (isMirrorTaskId) {
        console.info(
          'FocusModeTaskService.completeTask: mirror ID – skipping CRM patch',
          {
            taskId,
          }
        );
      } else {
        await this.#taskCompletionService?.complete(taskId);
      }
    } catch (err) {
      if (err?.message?.includes('auth not ready')) {
        console.warn(
          'FocusModeTaskService.completeTask: auth not ready (retry later)',
          { taskId }
        );
      } else {
        console.warn(
          'FocusModeTaskService.completeTask: CRM completion failed',
          {
            taskId,
            err,
          }
        );
      }
    }

    const extra = endSequence ? { endSequence: true } : {};

    const mirrorRef = docWithTenant(tenantId, `mirrorTasks/${taskId}`);
    await writeWithTenant(
      tenantId,
      mirrorRef,
      {
        status: 'CLOSED',
        completedAt: serverTimestamp(),
        ...extra,
      },
      { merge: true }
    ).catch((err) => {
      console.warn('FocusModeTaskService.completeTask: mirror write failed', {
        taskId,
        err,
      });
    });

    const taskRef = docWithTenant(tenantId, `tasks/${taskId}`);
    await writeWithTenant(
      tenantId,
      taskRef,
      {
        status: 'COMPLETED',
        completedAt: serverTimestamp(),
        ...extra,
      },
      { merge: true }
    ).catch((err) => {
      console.warn(
        'FocusModeTaskService.completeTask: tenant task write failed',
        {
          taskId,
          err,
        }
      );
    });
  }
}
